import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import type {
  PendingInputRequest,
  SubmitInputResponseArgs,
} from '../../../hooks/useWorkspace';

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  border-radius: 12px;
  border: 1px solid var(--accent, #6366f1);
  background: var(--accent-subtle, rgba(99, 102, 241, 0.08));
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent, #6366f1);
  flex-shrink: 0;
`;

const Label = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  flex: 1;
`;

const Title = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Subtitle = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const QuickPickWrap = styled.div`
  position: relative;
`;

const QuickPickBtn = styled.button`
  padding: 0.375rem 0.625rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg-surface);
  font-family: inherit;
  font-size: 0.75rem;
  color: var(--text-secondary);
  cursor: pointer;

  &:hover {
    background: var(--bg-elevated);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuickPickMenu = styled.ul`
  position: absolute;
  right: 0;
  bottom: calc(100% + 4px);
  list-style: none;
  padding: 0.25rem;
  margin: 0;
  min-width: 180px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  z-index: 10;
`;

const QuickPickItem = styled.li`
  padding: 0.375rem 0.5rem;
  font-size: 0.8125rem;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-primary);

  &:hover {
    background: var(--accent-subtle, rgba(99, 102, 241, 0.08));
  }
`;

const AnchorLink = styled.button`
  padding: 0.375rem 0.625rem;
  border: none;
  background: transparent;
  color: var(--accent, #6366f1);
  font-family: inherit;
  font-size: 0.75rem;
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background: var(--bg-surface);
  }
`;

interface AgentInputRequestBannerProps {
  request: PendingInputRequest;
  onJumpTo: (turnId: string) => void;
  onSubmit: (args: SubmitInputResponseArgs) => void;
  onStop?: () => void;
}

/**
 * Slim banner that replaces the prompt textarea while a structured input
 * request is outstanding. Keeps the user's focus on answering — Jump to
 * question scrolls the inline card into view; a quick-pick dropdown shows
 * the top 5 options for one-click single_choice answers; Stop remains
 * accessible to cancel the whole session.
 */
const AgentInputRequestBanner: React.FC<AgentInputRequestBannerProps> = ({
  request,
  onJumpTo,
  onSubmit,
  onStop,
}) => {
  const [quickPickOpen, setQuickPickOpen] = useState(false);
  const options = request.inputSchema.options ?? [];
  const isSingleChoice = request.inputSchema.inputType === 'single_choice';
  const quickOptions = useMemo(() => options.slice(0, 5), [options]);

  const handleQuickPick = useCallback(
    (optionId: string) => {
      const opt = options.find((o) => o.id === optionId);
      if (!opt) return;
      setQuickPickOpen(false);
      onSubmit({ answer: opt.id, humanReadableAnswer: opt.label });
    },
    [options, onSubmit],
  );

  return (
    <Banner role="status" aria-live="polite">
      <Dot aria-hidden="true" />
      <Label>
        <Title>Waiting for your input</Title>
        <Subtitle title={request.prompt}>{request.prompt}</Subtitle>
      </Label>
      <AnchorLink type="button" onClick={() => onJumpTo(request.turnId)}>
        Jump to question
      </AnchorLink>
      {isSingleChoice && quickOptions.length > 0 && (
        <QuickPickWrap>
          <QuickPickBtn
            type="button"
            onClick={() => setQuickPickOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={quickPickOpen}
          >
            Quick pick &#9662;
          </QuickPickBtn>
          {quickPickOpen && (
            <QuickPickMenu role="listbox">
              {quickOptions.map((o) => (
                <QuickPickItem
                  key={o.id}
                  role="option"
                  aria-selected="false"
                  onClick={() => handleQuickPick(o.id)}
                >
                  {o.label}
                </QuickPickItem>
              ))}
            </QuickPickMenu>
          )}
        </QuickPickWrap>
      )}
      {onStop && (
        <QuickPickBtn type="button" onClick={onStop} title="Stop session">
          Stop
        </QuickPickBtn>
      )}
    </Banner>
  );
};

export default AgentInputRequestBanner;
